import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IconCheck, IconFileCheck, IconX } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import {
  findTalentSubscription,
  getTalentImprovementAnswers,
  getTalentSubscription,
  submitTalentImprovementSkills,
  talentProfile,
  talentRoles,
  talentSubscribe,
} from "../../../services/talentCalls";
import { errorHandler } from "../../../utils";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import { DEFAULT_TALENT_SUBSCRIPTION } from "../../../constants";
import testStyles from "../Test/testRedesign.module.css";
import { UserContext } from "../../../context";
import Reveal from "../../../components/Reveal/Reveal";
import AiStatsShelf from "../../../components/Onboarding/Review/AiInsights/AiStatsShelf";
import ImproveOnCard from "../../../components/Onboarding/Review/AiInsights/ImproveOnCard";
import LearningPlanCard from "../../../components/Onboarding/Review/AiInsights/LearningPlanCard";
import styles from "../../../components/Onboarding/Review/AiInsights/AiInsights.module.css";
import TalentAiSummaryCard from "../../../components/Onboarding/Review/AiInsights/AiSummaryCard";
import TalentScoreComparisonCard from "../../../components/Onboarding/Review/AiInsights/AiInsightsVisual";
import { mapToScoreComparisonData } from "../../ClientDashboard/TalentProfile/components/ScoreComparison";
import { SelectSkillModal, TestModal } from "../../../components/LearningPath/improvement";

// Growth report emails deep link to /ai-insights?section=premium. A query param
// rather than a #hash because login only carries the path and query across sign-in.
const PREMIUM_SECTION_ID = "premium-insights";
const PREMIUM_SECTION_PARAM = "premium";

const AiInsights = () => {
  const [load, setLoad] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [activeSubscripion, setActiveSubscripion] = useState(null);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [disclaimerModal, setDisclaimerModal] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { skillModal, setSkillModal } = useContext(UserContext);
  const [improvementTestResult, setImprovementTestResult] = useState([]);
  const [role, setRole] = useState(null);
  const [skillLoading, setSkillLoading] = useState(false);
  const [allTalentRoles, setAllTalentRoles] = useState([]);
  const [test, setTest] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [openTestModal, setOpenTestModal] = useState(false);
  const [improveIndex, setImproveIndex] = useState(0);
  const [learningIndex, setLearningIndex] = useState(0);
  const [selectedPlanName, setSelectedPlanName] = useState(
    DEFAULT_TALENT_SUBSCRIPTION,
  ); // TODO: Update when new plans are added with cards

  const [data, setData] = useState({});
  const [searchParams] = useSearchParams();
  const [initialFetchesSettled, setInitialFetchesSettled] = useState(false);
  const hasScrolledToPremium = useRef(false);
  const handleRoleChange = (selected) => {
    setRole(selected);
  };

  const getUserProfile = async () => {
    try {
      setLoad(true);
      const res = await talentProfile();
      if (res?.data?.data?.talent) {
        setData(res?.data?.data?.talent);
      }
      setLoad(false);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };
  const handleGetSubscriptions = async () => {
    setSubscribeLoading(true);
    try {
      const res = await getTalentSubscription();
      if (res?.data?.data && Array.isArray(res?.data?.data?.records)) {
        const hasActiveSubscription = res?.data?.data?.records?.some(
          (sub) => sub?.isActive && sub?.paymentStatus === "completed",
        );
        setActiveSubscripion(hasActiveSubscription);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleFindTalentSubscription = async () => {
    try {
      const payload = {
        name: selectedPlanName,
      };
      const res = await findTalentSubscription(payload);
      const subscriptionId = res?.data?.data?.subscription?._id;
      if (subscriptionId) {
        return subscriptionId;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleTalentSubscribe = useCallback(async () => {
    setSubscribeLoading(true);
    try {
      const subscriptionId = await handleFindTalentSubscription();
      if (subscriptionId) {
        const res = await talentSubscribe(subscriptionId);
        if (res?.data?.data?.data?.data?.authorization_url) {
          window.location.href = res?.data?.data?.data?.data?.authorization_url;
        }
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeLoading(false);
    }
  }, [selectedPlanName]);

  const handleGetImprovementAnswers = async () => {
    setAnswersLoading(true);
    try {
      const res = await getTalentImprovementAnswers();
      if (res?.data?.data?.results) {
        setImprovementTestResult(res?.data?.data?.results);
      }
    } catch (error) {
      console.error("");
    } finally {
      setAnswersLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.allSettled([
        getUserProfile(),
        handleGetSubscriptions(),
        handleGetImprovementAnswers(),
        fetchTalentRoles(),
      ]);
      setInitialFetchesSettled(true);
    })();
  }, []);

  useEffect(() => {
    if (data?.hasActiveSubscription) {
      setActiveSubscripion(true);
    }
  }, [data]);

  useEffect(() => {
    if (test) {
      setSkillModal(false);
      setOpenTestModal(true);
    }
  }, [test]);

  // Land the reader on the locked cards instead of the top of the page. Waits for
  // the initial fetches so the cards are at their final height before scrolling -
  // the loading flags start out false, so keying off those would scroll on mount
  // while the page is still too short to move. Only ever runs once per visit.
  useEffect(() => {
    if (hasScrolledToPremium.current) return;
    if (searchParams.get("section") !== PREMIUM_SECTION_PARAM) return;
    if (!initialFetchesSettled) return;

    const target = document.getElementById(PREMIUM_SECTION_ID);
    if (!target) return;

    hasScrolledToPremium.current = true;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [searchParams, initialFetchesSettled]);

  const fetchTalentRoles = async () => {
    try {
      const res = await talentRoles();
      if (res?.data?.data?.records) {
        const sortedRoles = [...res?.data?.data?.records].sort((a, b) =>
          a?.name?.localeCompare(b?.name),
        );

        setAllTalentRoles(sortedRoles);
      }
    } catch (e) {
      console.error("");
    }
  };

  const handleSubmitSkill = async () => {
    setSkillLoading(true);
    try {
      const data = {
        skills: role ? [role.value] : [],
      };
      const res = await submitTalentImprovementSkills(data);
      if (res?.data?.success && res?.data?.data?.data?.test) {
        setTest(res?.data?.data?.data?.test);
      }
      if (res?.data?.success && res?.data?.data?.data?.attempt) {
        setAttempt(res?.data?.data?.data?.attempt);
      }
      if (!res?.data?.success || !res?.data?.data?.data?.test) {
        errorHandler(
          "You've already taken a test for this skill, or no test is available for it right now.",
          "Unable to start test",
        );
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSkillLoading(false);
    }
  };

  return (
    <div className="p-7 pb-12 max-[768px]:px-4 max-[768px]:py-5 max-[480px]:px-3.5 max-[480px]:py-4">
      {(load || answersLoading || subscribeLoading) && <FullScreenLoader />}

      <Reveal index={0} resetKey="mount">
        <AiStatsShelf
          overallScore={0}
          topStrength={0}
          growthArea={0}
          roleMatches={0}
        />
      </Reveal>

      <div className={styles.grid2col}>
        <div className={styles.gridCol}>
          <Reveal index={1} resetKey="mount">
            <TalentAiSummaryCard data={data} />
          </Reveal>
          <Reveal
            index={2}
            resetKey="mount"
            id={PREMIUM_SECTION_ID}
            className={styles.premiumAnchor}
          >
            <ImproveOnCard
              activeIndex={improveIndex}
              onSelect={setImproveIndex}
              handleTalentSubscribe={handleTalentSubscribe}
              subscribeLoading={subscribeLoading}
              isUnlock={activeSubscripion}
              data={improvementTestResult}
              handleGetImprovementAnswers={handleGetImprovementAnswers}
              submittedPsychometricTest={data?.submittedPsychometricTest}
              setDisclaimerModal={setDisclaimerModal}
            />
          </Reveal>
        </div>

        <div className={`${styles.gridCol} ${styles.colRight}`}>
          <Reveal index={1} resetKey="mount">
            <TalentScoreComparisonCard
              traits={mapToScoreComparisonData(data)}
            />
          </Reveal>
          <Reveal index={2} resetKey="mount">
            <LearningPlanCard
              activeIndex={learningIndex}
              onSelect={setLearningIndex}
              handleTalentSubscribe={handleTalentSubscribe}
              subscribeLoading={subscribeLoading}
              isUnlock={activeSubscripion}
              data={improvementTestResult}
              setDisclaimerModal={setDisclaimerModal}
            />
          </Reveal>
        </div>
      </div>
      {disclaimerModal && (
        <div className={testStyles.modalOverlay}>
          <div className={testStyles.modal}>
            <button
              className={testStyles.modalCloseBtn}
              onClick={() => setDisclaimerModal(false)}
              aria-label="Close"
            >
              <IconX size={15} />
            </button>
            <div className={testStyles.modalContent}>
              <div className={testStyles.modalIllustrationWrap}>
                <IconFileCheck size={48} color="#fff" />
              </div>
              <label className={testStyles.disclaimerRow}>
                <input
                  type="checkbox"
                  className={testStyles.checkboxInput}
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span className={testStyles.checkboxBox}>{agreed && <IconCheck size={15} />}</span>
                <span className={testStyles.disclaimerText}>
                  I acknowledge that Awasource will process my profile, CV, and
                  assessment responses, including through automated and AI-assisted
                  systems, to generate personalized AI Insights in accordance with
                  the Privacy Policy and I understand that these insights provide
                  guidance on my strengths, potential career paths, and
                  opportunities to help me make informed decisions. While individual
                  outcomes may vary, the insights are designed to support my
                  professional growth.
                </span>
              </label>
              <button
                className={testStyles.btnAmber}
                disabled={!agreed}
                onClick={() => {
                  setDisclaimerModal(false);
                  setAgreed(false);
                  setSkillModal(true);
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {skillModal && (
        <SelectSkillModal
          setSkillModal={setSkillModal}
          allTalentRoles={allTalentRoles}
          role={role}
          handleRoleChange={handleRoleChange}
          loading={skillLoading}
          handleSubmitSkill={handleSubmitSkill}
        />
      )}

      {openTestModal && (
        <TestModal
          test={test}
          setTest={setTest}
          attempt={attempt}
          setAttempt={setAttempt}
          setOpenTestModal={setOpenTestModal}
          handleGetImprovementAnswers={handleGetImprovementAnswers}
        />
      )}
    </div>
  );
};

export default AiInsights;
